/**
 * Site Diary theme tokens (Warm Paper variant).
 *
 * Central palette for the diary module. All diary components import from
 * this file so the visual language stays consistent and future theme
 * tweaks touch one place.
 *
 * Colours were chosen to evoke a physical site diary: cream paper
 * background, white cards, ink-dark text, amber accents for the primary
 * CTA. Icon chips use saturated per-type foreground colours on tinted
 * backgrounds so entry types read at a glance.
 */

import {
  FileText, Users, CloudRain, AlertOctagon,
  HelpCircle, ClipboardCheck, UserCheck, Package,
  Truck, Camera, HardHat, Images,
} from 'lucide-react';

// ---------- Surface palette ------------------------------------------------
export const PAPER = {
  page:        '#FAF7F2',        // page background — cream paper
  paperMuted:  '#F3F0E9',        // subtle recessed surfaces (metadata pills)
  card:        '#FFFFFF',        // primary card surface
  border:      '#E7E2D8',        // hairline paper edge
  divider:     '#EEE8DC',        // even softer separator
  ink:         '#1F2937',        // primary text (deep charcoal)
  inkSoft:     '#4B5563',        // secondary text
  muted:       '#8B8378',        // metadata / captions (warm grey)
  hint:        '#B8B0A3',        // placeholder / softest hint
  accent:      '#F5A623',        // brand amber
  accentInk:   '#0F1520',        // text on accent bg
  navy:        '#1F2937',        // dark bar for CTAs/header logo
  shadow:      '0 1px 2px rgba(0,0,0,0.04), 0 1px 1px rgba(0,0,0,0.02)',
};

// ---------- Type palette ---------------------------------------------------
// Each type gets an icon + saturated foreground / tinted background pair
// used to render the round icon chips. `dot` is the fallback dot colour
// for tight contexts (e.g. inline in a small pill).
//
// Weather is intentionally NOT in the quick-add tile list — daily weather
// is auto-populated at the top of the DayView from Open-Meteo. It stays in
// this metadata map because legacy weather entries in the DB still render
// on the timeline; only its quick-add tile has been retired.
//
// DIA-1 §2.5 — Delivery tile is retired (replaced by Capture), but the
// `delivery` type metadata is kept here so pre-existing delivery entries
// keep rendering correctly on the timeline.
export const TYPE_META = {
  note:        { label: 'Note',            icon: FileText,       fg: '#64748B', bg: '#F1F5F9', ring: '#CBD5E1', dot: '#94A3B8' },
  labour:      { label: 'Labour',          icon: Users,          fg: '#2563EB', bg: '#DBEAFE', ring: '#93C5FD', dot: '#3B82F6' },
  delivery:    { label: 'Delivery',        icon: Truck,          fg: '#059669', bg: '#D1FAE5', ring: '#6EE7B7', dot: '#10B981' },
  materials:   { label: 'Order/Enquiry',   icon: Package,        fg: '#C2410C', bg: '#FFEDD5', ring: '#FDBA74', dot: '#EA580C' },
  weather:     { label: 'Weather',         icon: CloudRain,      fg: '#0891B2', bg: '#CFFAFE', ring: '#67E8F9', dot: '#06B6D4' },
  delay:       { label: 'Delay',           icon: AlertOctagon,   fg: '#DC2626', bg: '#FEE2E2', ring: '#FCA5A5', dot: '#EF4444' },
  rfi:         { label: 'RFI',             icon: HelpCircle,     fg: '#D97706', bg: '#FEF3C7', ring: '#FCD34D', dot: '#F59E0B' },
  instruction: { label: 'Instruction',     icon: ClipboardCheck, fg: '#7C3AED', bg: '#EDE9FE', ring: '#C4B5FD', dot: '#8B5CF6' },
  visit:       { label: 'Site Visit',      icon: UserCheck,      fg: '#4F46E5', bg: '#E0E7FF', ring: '#A5B4FC', dot: '#6366F1' },
  capture:     { label: 'Cost Capture',    icon: Camera,         fg: '#0EA5E9', bg: '#E0F2FE', ring: '#7DD3FC', dot: '#38BDF8' },
  bc_capture:  { label: 'Building Control', icon: HardHat,        fg: '#0D9488', bg: '#CCFBF1', ring: '#5EEAD4', dot: '#14B8A6' },
  // One-tap site photos (shutter → straight into Site Photos/DD-MM-YYYY).
  photo:       { label: 'Site photos',     icon: Images,         fg: '#BE185D', bg: '#FCE7F3', ring: '#F9A8D4', dot: '#EC4899' },
};

// Ordered list of types that render as tiles in the quick-add grid.
// Row 1 = daily use; Row 2 = contractual record. (DIA-1 §2.5.)
export const QUICK_ADD_TYPES = [
  'labour', 'capture', 'bc_capture', 'materials',
  'delay',  'rfi',     'instruction', 'visit',
];

export const typeMeta = (type) => TYPE_META[type] || TYPE_META.note;

// ---------- Project colour palette (light-friendly) -----------------------
// Mirrors the 12 project colours in the backend `DIARY_COLOURS` map, but
// tuned for a light background — the raw hex is used for the coloured
// ribbon on project cards / banners.
export const PROJECT_COLOURS = {
  blue:    { hex: '#3B82F6', chip: '#DBEAFE', chipInk: '#1D4ED8' },
  emerald: { hex: '#10B981', chip: '#D1FAE5', chipInk: '#047857' },
  purple:  { hex: '#8B5CF6', chip: '#EDE9FE', chipInk: '#6D28D9' },
  amber:   { hex: '#F59E0B', chip: '#FEF3C7', chipInk: '#B45309' },
  pink:    { hex: '#EC4899', chip: '#FCE7F3', chipInk: '#BE185D' },
  cyan:    { hex: '#06B6D4', chip: '#CFFAFE', chipInk: '#0E7490' },
  red:     { hex: '#EF4444', chip: '#FEE2E2', chipInk: '#B91C1C' },
  indigo:  { hex: '#6366F1', chip: '#E0E7FF', chipInk: '#4338CA' },
  teal:    { hex: '#14B8A6', chip: '#CCFBF1', chipInk: '#0F766E' },
  orange:  { hex: '#F97316', chip: '#FFEDD5', chipInk: '#C2410C' },
  lime:    { hex: '#84CC16', chip: '#ECFCCB', chipInk: '#4D7C0F' },
  fuchsia: { hex: '#D946EF', chip: '#FAE8FF', chipInk: '#A21CAF' },
};

export const projectColour = (project) =>
  PROJECT_COLOURS[project?.colour] || PROJECT_COLOURS.blue;

// ---------- Shared class strings ------------------------------------------
// Reused across every form input so the entire diary looks consistent
// without every file re-declaring the same Tailwind chain.
export const INPUT_CLASS =
  'w-full bg-white border border-[#E7E2D8] rounded-lg px-3 py-2.5 text-[14px] text-[#1F2937] placeholder-[#B8B0A3] focus:outline-none focus:border-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/20';

export const CARD_CLASS =
  'bg-white border border-[#E7E2D8] rounded-xl shadow-[0_1px_2px_rgba(0,0,0,0.04)]';

// The little pill used for uppercase section labels.
export const SECTION_LABEL_CLASS =
  'block text-[11px] uppercase tracking-wider font-semibold text-[#8B8378]';

// Amber primary CTA (dark bar with amber text — matches Ops-app-toggle in
// header). Kept as a class chain so buttons of any width can adopt it.
export const CTA_PRIMARY_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-[#1F2937] text-[#F5A623] font-semibold text-[14px] px-4 py-3 active:scale-[0.98] disabled:opacity-50 transition-transform';

// Ghost button — used for Cancel / secondary actions.
export const CTA_GHOST_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-white border border-[#E7E2D8] text-[#4B5563] font-medium text-[14px] px-4 py-3 hover:bg-[#FAF7F2] disabled:opacity-50';
